import React from 'react';
import { Box, Typography, Stack, Divider } from '@mui/material';

import { DASH, EVENT_TONES } from '../dashboard/dashboardTokens';
import { EmptyState } from '../dashboard/Surface';
import DayHourAxis from './DayHourAxis';
import DayTimeline from './DayTimeline';

// Zvyk sa dá porovnávať až keď je z čoho — pri dvoch dňoch je „priemer"
// len ten druhý deň.
const MIN_DAYS_FOR_HABIT = 3;
// Pod päť minút je to šum meraný na pípnutie, nie zmena správania.
const DELTA_NOISE_MIN = 5;

const toMinutes = (time) => {
    const match = time ? String(time).match(/^(\d{1,2}):(\d{2})/) : null;
    return match ? Number(match[1]) * 60 + Number(match[2]) : null;
};

const formatSpan = (minutes) => {
    const total = Math.max(Math.round(minutes), 0);
    const h = Math.floor(total / 60);
    const m = total % 60;
    if (h === 0) return `${m} m`;
    return m === 0 ? `${h} h` : `${h} h ${m} m`;
};

/**
 * Jedno číslo dňa a pod ním veta, o koľko sa líši od zvyku. Samotné „09:23"
 * nepovie nič — až „o 12 min neskôr než zvyčajne" je informácia.
 */
const DayStat = ({ label, value, delta, muted }) => (
    <Box sx={{ minWidth: 0 }}>
        <Typography
            variant="caption"
            sx={{
                display: 'block',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                fontSize: '0.6875rem',
                fontWeight: 600,
                color: 'text.secondary',
            }}
        >
            {label}
        </Typography>
        <Typography
            sx={{
                fontFamily: DASH.display,
                fontSize: { xs: '1.5rem', md: '1.75rem' },
                fontWeight: 600,
                letterSpacing: '-0.02em',
                lineHeight: 1.15,
                mt: 0.25,
                color: muted ? 'text.disabled' : 'text.primary',
                ...DASH.numeric,
            }}
        >
            {value}
        </Typography>
        {delta && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                {delta}
            </Typography>
        )}
    </Box>
);

const Badge = ({ tone, children }) => (
    <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{
            px: 1.25,
            py: 0.375,
            borderRadius: '999px',
            bgcolor: `${tone}1F`,
            flexShrink: 0,
        }}
    >
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: tone }} />
        <Typography variant="caption" sx={{ fontWeight: 600 }}>{children}</Typography>
    </Stack>
);

/**
 * Vybraný deň rozložený tak, aby sa dal prečítať bez počítania: veľké čísla,
 * pod nimi tvar dňa na osi a nakoniec surové pípnutia.
 */
const DayDetail = ({ day, events, totals, t }) => {
    const hasAnything = Boolean(day) || (events && events.length > 0);
    if (!hasAnything) {
        return (
            <EmptyState
                title={t('pages.employeeProfile.day.nothing')}
                hint={t('pages.employeeProfile.day.nothingHint')}
            />
        );
    }

    const comparable = (totals?.daysWorked || 0) >= MIN_DAYS_FOR_HABIT;

    const describeDelta = (actual, average, kind) => {
        if (!comparable || actual == null || average == null) return null;
        const diff = Math.round(actual - average);
        if (Math.abs(diff) < DELTA_NOISE_MIN) return t('pages.employeeProfile.day.deltaSame');
        const amount = formatSpan(Math.abs(diff));
        const key = kind === 'clock'
            ? (diff > 0 ? 'deltaLater' : 'deltaEarlier')
            : (diff > 0 ? 'deltaMore' : 'deltaLess');
        return t(`pages.employeeProfile.day.${key}`).replace('{value}', amount);
    };

    const arrivalMin = toMinutes(day?.firstIn);
    const departureMin = toMinutes(day?.realOut);
    const workplace = (events || []).map((e) => e.label).find(Boolean);

    const stats = [
        {
            key: 'arrival',
            label: t('pages.dashboard.analytics.avgArrival'),
            value: day?.firstIn || '—',
            muted: !day?.firstIn,
            delta: describeDelta(arrivalMin, totals?.avgArrivalMin, 'clock'),
        },
        {
            key: 'departure',
            label: t('pages.dashboard.analytics.avgDeparture'),
            // Auto-ukončený deň nemá odpípnutý odchod — vypísať systémový čas
            // ako odchod by bola lož, na ktorej sa dá postaviť výplata.
            value: day?.realOut || (day?.openShift
                ? t('pages.employeeProfile.strip.open')
                : day?.autoEnded ? t('pages.employeeProfile.day.noEndShort') : '—'),
            muted: !day?.realOut,
            delta: describeDelta(departureMin, totals?.avgDepartureMin, 'clock'),
        },
        {
            key: 'work',
            label: t('pages.dashboard.analytics.avgWork'),
            value: day?.netMinutes ? formatSpan(day.netMinutes) : '—',
            muted: !day?.netMinutes,
            delta: day?.netMinutes ? describeDelta(day.netMinutes, totals?.avgNetMinutes, 'amount') : null,
        },
        {
            key: 'break',
            label: t('pages.dashboard.analytics.avgBreak'),
            value: day?.breakMinutes ? formatSpan(day.breakMinutes) : '—',
            muted: !day?.breakMinutes,
            delta: day?.breakMinutes ? describeDelta(day.breakMinutes, totals?.avgBreakMinutes, 'amount') : null,
        },
    ];

    const badges = [
        day?.openShift ? { key: 'open', tone: EVENT_TONES.arrival, text: t('pages.employeeProfile.strip.open') } : null,
        day?.autoEnded ? { key: 'auto', tone: EVENT_TONES.break_start, text: t('pages.employeeProfile.strip.auto') } : null,
        day?.statusName ? { key: 'status', tone: '#7C3AED', text: day.statusName } : null,
        day?.shifts > 1 ? {
            key: 'shifts',
            tone: EVENT_TONES.departure,
            text: t('pages.employeeProfile.day.shiftCount').replace('{count}', String(day.shifts)),
        } : null,
    ].filter(Boolean);

    const nowMinutes = day?.openShift ? new Date().getHours() * 60 + new Date().getMinutes() : null;

    return (
        <Box>
            {(badges.length > 0 || workplace) && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                    {badges.map((badge) => (
                        <Badge key={badge.key} tone={badge.tone}>{badge.text}</Badge>
                    ))}
                    {workplace && (
                        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                            {workplace}
                        </Typography>
                    )}
                </Stack>
            )}

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                    columnGap: 2,
                    rowGap: 2.5,
                }}
            >
                {stats.map((stat) => (
                    <DayStat key={stat.key} {...stat} />
                ))}
            </Box>

            <DayHourAxis
                day={day}
                events={events}
                avgArrivalMin={comparable ? totals?.avgArrivalMin : null}
                avgDepartureMin={comparable ? totals?.avgDepartureMin : null}
                nowMinutes={nowMinutes}
                t={t}
            />

            {events && events.length > 0 && (
                <>
                    <Divider sx={{ my: 2.5 }} />
                    <DayTimeline events={events} t={t} />
                </>
            )}
        </Box>
    );
};

export default DayDetail;
